import React, { useEffect, useState } from 'react';
import { backupAPI } from '../services/api';
import DataTable from '../components/DataTable';

const JOB_COLS = [
  { key: 'customer_name',    label: 'Cliente' },
  { key: 'job_name',         label: 'Job' },
  { key: 'agent_label',      label: 'Agente' },
  { key: 'vm_name',          label: 'VM' },
  { key: 'repository',       label: 'Repository' },
  { key: 'backup_type',      label: 'Tipo' },
  { key: 'duration_minutes', label: 'Durata (min)' },
  { key: 'end_time',         label: 'Ultimo Run' },
  { key: 'next_run',         label: 'Prossimo Run' },
  { key: 'status',           label: 'Stato' },
];

const RETENTION_COLS = [
  { key: 'customer_name',    label: 'Cliente' },
  { key: 'job_name',         label: 'Job' },
  { key: 'vm_name',          label: 'VM' },
  { key: 'repository',       label: 'Repository' },
  { key: 'restore_points_count', label: 'Punti di Ripristino' },
  { key: 'retention_summary',    label: 'Retention' },
];

const PROBLEM_COLS = [
  { key: 'customer_name', label: 'Cliente' },
  { key: 'job_name',      label: 'Job' },
  { key: 'repository',    label: 'Repository' },
  { key: 'backup_type',   label: 'Tipo' },
  { key: 'end_time',      label: 'Ultimo Run' },
  { key: 'next_run',      label: 'Prossimo Run' },
  { key: 'status',        label: 'Stato' },
  { key: 'failure_cause', label: 'Causa' },
];

// Etichetta leggibile per le categorie di fallimento più comuni prodotte dal
// modulo di classificazione automatica (failure_normalizer); se una
// categoria non è nell'elenco, si formatta comunque in modo leggibile
// (sostituendo gli underscore e mettendo in minuscolo) invece di lasciare
// lo SNAKE_CASE grezzo in dashboard.
const FAILURE_CATEGORY_LABELS = {
  STORAGE_BACKEND_UNAVAILABLE: 'Storage non disponibile',
  BACKUP_INTERRUPTED:          'Backup interrotto',
  STORAGE_FULL:                'Spazio esaurito',
  AUTH_ERROR:                  'Errore di autenticazione',
  NETWORK_TIMEOUT:             'Timeout di rete',
  TLS_CERTIFICATE_ERROR:       'Errore certificato TLS',
  GUEST_AGENT_ISSUE:           'Problema guest agent',
  AGENT_UNREACHABLE:           'Macchina irraggiungibile',
  AGENT_OFFLINE:               'Nessun check-in agent',
  REPOSITORY_WRITE_FAILURE:    'Scrittura repository fallita',
  STORAGE_DISK_READ_TRUNCATED: 'Lettura disco sorgente interrotta',
  RPC_TIMEOUT:                 'Timeout comunicazione RPC',
  UNKNOWN_ERROR:               'Causa non riconosciuta',
};

function formatFailureCategory(category) {
  if (!category) return null;
  return FAILURE_CATEGORY_LABELS[category] ||
    category.replace(/_/g, ' ').toLowerCase().replace(/^\w/, c => c.toUpperCase());
}

const OVERDUE_COLS = [
  { key: 'customer_name', label: 'Cliente' },
  { key: 'job_name',      label: 'Job' },
  { key: 'vm_name',       label: 'VM' },
  { key: 'repository',    label: 'Repository' },
  { key: 'backup_type',   label: 'Tipo' },
  { key: 'status',        label: 'Ultimo Stato' },
  { key: 'end_time',      label: 'Ultimo Run' },
  { key: 'hours_ago',     label: 'Ore fa' },
  { key: 'severity',      label: 'Gravità' },
];

// Etichetta leggibile per il metodo di rilevamento, mostrata come dettaglio
// (tooltip) sul pallino colorato, non come colonna testuale separata.
const DETECTION_METHOD_LABELS = {
  scheduler:     'Calcolato in base all\'orario di schedulazione previsto',
  storico:       'Calcolato in base al ritmo abituale di questo job',
  soglia_fissa:  'Calcolato con soglia standard (nessuno storico disponibile)',
};

function severityFromRatio(hoursAgo, thresholdHours) {
  if (!hoursAgo || !thresholdHours) return { color: '#f59e0b', label: 'Da verificare' };
  const ratio = hoursAgo / thresholdHours;
  if (ratio >= 2) return { color: '#dc2626', label: 'Molto in ritardo' };
  return { color: '#f59e0b', label: 'Appena scaduto' };
}

const VM_COLS = [
  { key: 'customer_name', label: 'Cliente' },
  { key: 'end_time',      label: 'Data' },
  { key: 'backup_type',   label: 'Tipo Backup' },
  { key: 'job_name',      label: 'Job' },
  { key: 'repository',    label: 'Repository' },
  { key: 'vm_name',       label: 'VM' },
  { key: 'status',        label: 'Stato' },
];

export default function OverviewPage({ customer }) {
  const [data, setData]         = useState(null);
  const [loading, setLoading]   = useState(true);
  const [exporting, setExporting] = useState(null); // 'xlsx' | 'pdf' | null

  useEffect(() => {
    setLoading(true);
    backupAPI.getOverview(customer)
      .then(r => setData(r.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [customer]);

  const handleExport = async (format) => {
    setExporting(format);
    try {
      const token = localStorage.getItem('auth_token');
      const params = customer ? `?customer=${encodeURIComponent(customer)}` : '';
      const res = await fetch(`/api/export/overview.${format}${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Export fallito');

      const disposition = res.headers.get('Content-Disposition') || '';
      const match = disposition.match(/filename="(.+)"/);
      const filename = match ? match[1] : `overview.${format}`;

      const blob = await res.blob();
      const url  = window.URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href = url; a.download = filename;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error(err);
      alert("Errore durante l'export. Riprova.");
    } finally {
      setExporting(null);
    }
  };

  if (loading) return <div style={s.loading}>Caricamento...</div>;
  if (!data)   return <div style={s.loading}>Errore nel caricamento dati</div>;

  const { stats, jobs, problems, overdue, vm_failed, retention_data } = data;
  const successRate = stats?.total > 0
    ? Math.round((stats.ok_count / stats.total) * 100)
    : 0;
  const rateColor = successRate === 100 ? '#16a34a' : successRate >= 80 ? '#ca8a04' : '#dc2626';

  return (
    <div>
      <div style={s.headerRow}>
        <h2 style={s.pageTitle}>Overview</h2>
        <div style={s.exportButtons}>
          <button onClick={() => handleExport('xlsx')} disabled={!!exporting} style={s.btnExport}>
            {exporting === 'xlsx' ? '⏳ Generazione...' : '📊 Esporta Excel'}
          </button>
          <button onClick={() => handleExport('pdf')} disabled={!!exporting} style={s.btnExport}>
            {exporting === 'pdf' ? '⏳ Generazione...' : '📄 Esporta PDF'}
          </button>
        </div>
      </div>

      {/* KPI */}
      <div style={s.kpiRow}>
        <KPICard label="Backup Success Rate" value={`${successRate}%`} color={rateColor}
          sub="basato sull'ultimo run per job" />
        <KPICard label="Job OK"      value={stats?.ok_count      ?? 0} color="#16a34a" />
        <KPICard label="Job FAILED"  value={stats?.failed_count  ?? 0} color="#dc2626" />
        <KPICard label="Job WARNING" value={stats?.warning_count ?? 0} color="#ca8a04" />
        <KPICard label="Totale Job"  value={stats?.total         ?? 0} color="#2563eb" />
      </div>

      {/* Problemi attivi */}
      {problems?.length > 0 && (
        <DataTable
          title={`🚨 Job con problemi attivi — ${problems.length}`}
          columns={PROBLEM_COLS}
          rows={problems.map(r => {
            const label = formatFailureCategory(r.failure_category);
            const tooltipParts = [r.failure_explanation, r.failure_action ? `Azione consigliata: ${r.failure_action}` : null]
              .filter(Boolean);
            return {
              ...r,
              failure_cause: label ? (
                <span title={tooltipParts.join(' — ')} style={{ cursor: 'help', borderBottom: '1px dotted #94a3b8' }}>
                  {label}
                </span>
              ) : '—',
            };
          })}
        />
      )}

      {/* Job scaduti */}
      {overdue?.length > 0 && (
        <DataTable
          title={`⏰ Job non eseguiti da più di 24 ore — ${overdue.length}`}
          columns={OVERDUE_COLS}
          rows={overdue.map(r => {
            const sev = severityFromRatio(parseFloat(r.hours_ago), parseFloat(r.threshold_hours));
            return {
              ...r,
              hours_ago: r.hours_ago ? `${parseFloat(r.hours_ago).toFixed(0)}h` : '—',
              severity: (
                <span
                  title={DETECTION_METHOD_LABELS[r.detection_method] || ''}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'help' }}
                >
                  <span style={{
                    width: 10, height: 10, borderRadius: '50%',
                    background: sev.color, display: 'inline-block', flexShrink: 0,
                  }} />
                  {sev.label}
                </span>
              ),
            };
          })}
        />
      )}

      {/* VM FAILED */}
      <DataTable
        title={`⛔ VM Attualmente FAILED — ${vm_failed?.length ?? 0}`}
        columns={VM_COLS}
        rows={vm_failed}
        emptyMsg="Nessuna VM in stato FAILED ✅"
      />

      {/* Tutti i job correnti */}
      <DataTable
        title={`Stato Attuale Job — ${jobs?.length ?? 0} job`}
        columns={JOB_COLS}
        rows={jobs}
        emptyMsg="Nessun job trovato"
      />

      {/* Punti di ripristino e retention, per VM */}
      <DataTable
        title="Punti di Ripristino e Retention"
        columns={RETENTION_COLS}
        rows={retention_data}
        emptyMsg="Nessun dato di retention disponibile"
      />
    </div>
  );
}

function KPICard({ label, value, color, sub }) {
  return (
    <div style={{ ...s.kpiCard, borderTop: `4px solid ${color}` }}>
      <div style={{ fontSize: 32, fontWeight: 800, color }}>{value}</div>
      <div style={{ fontSize: 13, color: '#64748b', marginTop: 4 }}>{label}</div>
      {sub && <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

const s = {
  pageTitle: { margin: 0, fontSize: 22, fontWeight: 700, color: '#1e293b' },
  headerRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, flexWrap: 'wrap', gap: 12 },
  exportButtons: { display: 'flex', gap: 10 },
  btnExport: {
    padding: '9px 16px', borderRadius: 8, border: '1.5px solid #d1d5db',
    background: '#fff', color: '#374151', fontWeight: 600, fontSize: 13,
    cursor: 'pointer', whiteSpace: 'nowrap',
  },
  loading:   { padding: 40, textAlign: 'center', color: '#64748b' },
  kpiRow:    { display: 'flex', gap: 16, marginBottom: 24, flexWrap: 'wrap' },
  kpiCard:   {
    flex: '1 1 140px', background: '#fff', borderRadius: 12,
    padding: '20px 24px', border: '1px solid #e2e8f0',
    boxShadow: '0 1px 4px rgba(0,0,0,0.05)',
  },
};
